import { Component, OnInit, OnDestroy, OnChanges, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { takeUntil, finalize } from "rxjs/operators";
import { IDashboardWidget } from "src/app/models/dashboard";
import { SampleService } from "src/app/services/sample.service";
import { CommonService } from "src/app/services/common.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { WidgetOrgFilterComponent } from "src/app/common-components/widget-org-filter/widget-org-filter.component";
import { navigateToGridWithOrgFilter } from "src/app/constants/dashboard-navigation.utils";
import { SAMPLE_GRID_ROUTE } from "src/app/constants/dashboard.constants";
import { DATE_FILTER_TYPE_ENUM } from "src/app/shared/grid/baseGrid.component";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    SampleStatusEnum,
    SampleStatusColors,
} from "src/app/enums/sampleStatus.enum";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { SampleCountByStatus } from "src/app/models/sample-status-summary.model";
import { getNamedEnum } from "src/app/_helpers/enum-helper";
import { SampleStatusFiltersComponent } from "./sample-status-filters.component";
import {
    SampleStatusFilterValues,
    SAMPLE_STATUS_FILTER_DEFAULTS,
    configToFilterValues,
} from "./sample-status-widget.constants";
import { BaseDashboardWidget } from "src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget";
import { resolveDateFilterRange } from "../_shared/date-filter.util";

import { trackByIndex } from "src/app/shared/utils/track-by.util";
@Component({
    standalone: true,
    selector: "app-sample-status-widget",
    imports: [
        CommonModule,
        TranslateModule,
        MatIconModule,
        SampleStatusFiltersComponent,
        MatTooltipModule,
        WidgetOrgFilterComponent,
    ],
    templateUrl: "./sample-status-widget.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./sample-status-widget.component.scss"]
})
export class SampleStatusWidgetComponent
    extends BaseDashboardWidget
    implements OnInit, OnDestroy, OnChanges, IDashboardWidget
{
    statusItems: {
        status: SampleStatusEnum;
        label: string;
        count: number;
        color: string;
    }[] = [];
    digitsClass: '' | 'digits-3' | 'digits-4' = '';

    tempFilterValues: SampleStatusFilterValues = {
        ...SAMPLE_STATUS_FILTER_DEFAULTS,
    };

    private statusNameMap = getNamedEnum(SampleStatusEnum);
    private readonly displayOrder = [
        SampleStatusEnum.Planned,
        SampleStatusEnum.Draft,
        SampleStatusEnum.Ongoing,
        SampleStatusEnum.AwaitingPostSampleInfo,
        SampleStatusEnum.AwaitingLabSubmission,
        SampleStatusEnum.SubmittedToLab,
        SampleStatusEnum.ReceivedByLab,
        SampleStatusEnum.AwaitingApproval,
        SampleStatusEnum.Approved,
        SampleStatusEnum.ApprovedLetterSent,
        SampleStatusEnum.Void,
        SampleStatusEnum.VoidLetterSent,
    ];

    constructor(
        private sampleService: SampleService,
        private commonService: CommonService,
        private router: Router,
        private translate: TranslateService,
        private dateFormatService: DateFormatService,
    ) {
        super();
    }

    onConfigChange?(config: Record<string, unknown>): void {
        this.widgetConfig = config;
        this.syncFromConfig();
        this.reload();
    }

    onFilterChange(values: SampleStatusFilterValues): void {
        this.tempFilterValues = values;
        this.reload();
    }

    navigateToSamples(): void {
        const orgOverrideId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        navigateToGridWithOrgFilter(this.router, this.commonService, SAMPLE_GRID_ROUTE, this.buildDateFilterEntry(), orgOverrideId);
    }

    navigateToSamplesByStatus(status: SampleStatusEnum): void {
        const orgOverrideId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        const extra: Record<string, string> = {
            ...this.buildDateFilterEntry(),
            [nameOf<{ sampleStatus: number }>(x => x.sampleStatus)]: status.toString(),
        };
        navigateToGridWithOrgFilter(this.router, this.commonService, SAMPLE_GRID_ROUTE, extra, orgOverrideId);
    }

    private buildDateFilterEntry(): Record<string, string> {
        const payload = this.buildDatePayload(
            this.tempFilterValues.dateFilterType,
            this.tempFilterValues.customStartDate,
            this.tempFilterValues.customEndDate,
        );
        return payload ? { [DATE_FILTER_TYPE_ENUM]: payload } : {};
    }

    protected override syncFromConfig(): void {
        this.tempFilterValues = configToFilterValues(this.widgetConfig);
    }

    protected override reload(): void {
        this.loadStatusSummary();
    }

    private buildDatePayload(
        dateFilterType: number | undefined,
        customStartDate: string | null,
        customEndDate: string | null,
    ): string | null {
        if (
            dateFilterType == null ||
            dateFilterType === DateFilterTypeEnum.AllDates
        ) {
            return null;
        }
        let start: Date;
        let end: Date;
        if (dateFilterType === DateFilterTypeEnum.Custom) {
            if (!customStartDate || !customEndDate) {
                return null;
            }
            start = new Date(customStartDate);
            end = new Date(customEndDate);
        } else {
            const range = resolveDateFilterRange(dateFilterType);
            if (!range) {
                return null;
            }
            start = range.start;
            end = range.end;
        }
        return JSON.stringify({
            dateFilterType: DateFilterTypeEnum.Custom,
            startDate: this.dateFormatService.convertDateToTimestamp(start),
            endDate: this.dateFormatService.convertDateToTimestamp(end),
            previousStartDate: 0,
            previousEndDate: 0,
        });
    }

    private buildFilterBy(
        dateFilterType: number | undefined,
        customStartDate: string | null,
        customEndDate: string | null,
    ): string | undefined {
        const payload = this.buildDatePayload(dateFilterType, customStartDate, customEndDate);
        if (!payload) {
            return undefined;
        }
        return JSON.stringify({ [DATE_FILTER_TYPE_ENUM]: payload });
    }

    private loadStatusSummary(): void {
        this.isLoading = true;
        this.error = undefined;

        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();
        this.sampleService
            .getStatusSummary({
                selectedOrganizationLevelIds: effectiveOrgId ? [effectiveOrgId] : undefined,
                selectedStatuses: this.tempFilterValues.selectedStatuses?.length
                    ? this.tempFilterValues.selectedStatuses
                    : undefined,
                filterBy: this.buildFilterBy(
                    this.tempFilterValues.dateFilterType,
                    this.tempFilterValues.customStartDate,
                    this.tempFilterValues.customEndDate,
                ),
            })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (response) => this.mapStatusCounts(response.statusCounts),
                error: () => {
                    this.error = this.translate.instant(
                        "Failed to load sample status data",
                    );
                    this.statusItems = [];
                },
            });
    }

    private mapStatusCounts(statusCounts: SampleCountByStatus[]): void {
        const visibleStatuses = this.tempFilterValues.selectedStatuses?.length
            ? this.displayOrder.filter((s) =>
                  this.tempFilterValues.selectedStatuses.includes(s),
              )
            : this.displayOrder;

        const countMap = new Map(
            statusCounts.map((sc) => [sc.status, sc.count]),
        );
        this.statusItems = visibleStatuses.map((status) => ({
            status,
            label: this.statusNameMap[status] || status.toString(),
            count: countMap.get(status) || 0,
            color: SampleStatusColors[status],
        }));
        const maxDigits = this.statusItems.length
            ? Math.max(...this.statusItems.map((i) => String(i.count).length), 1)
            : 1;
        this.digitsClass = maxDigits >= 4 ? 'digits-4' : maxDigits === 3 ? 'digits-3' : '';
    }

    trackByStatus(_: number, item: { status: SampleStatusEnum }): SampleStatusEnum {
        return item.status;
    }

    public trackByIndex = trackByIndex;
}
